import { useEffect, useRef, useState } from "react";
import { API } from "@/lib/api";

/**
 * useLivePreview — Live Preview Thumbnails v1.
 * Returns a self-refreshing image src for a live stream card: the host's
 * still-frame snapshot (10s cadence) when one exists, else the static
 * thumbnail_url. Refresh is IntersectionObserver-gated so only on-screen
 * cards hit the network. On any fetch error we fall back permanently to
 * the static thumbnail for this mount.
 */
export default function useLivePreview(stream) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  const [tick, setTick] = useState(() => Math.floor(Date.now() / 10000));
  const [failed, setFailed] = useState(false);
  const hasPreview = !!stream?.preview_updated_at && !failed;

  useEffect(() => {
    const node = ref.current;
    if (!node || !hasPreview) return undefined;
    const io = new IntersectionObserver(
      ([entry]) => setVisible(entry.isIntersecting),
      { threshold: 0.1 },
    );
    io.observe(node);
    return () => io.disconnect();
  }, [hasPreview]);

  useEffect(() => {
    if (!visible || !hasPreview) return undefined;
    const t = setInterval(() => setTick(Math.floor(Date.now() / 10000)), 10_000);
    return () => clearInterval(t);
  }, [visible, hasPreview]);

  const src = hasPreview
    ? `${API}/live/${stream.id}/preview?t=${tick}`
    : stream?.thumbnail_url || null;

  return {
    ref,
    src,
    isLivePreview: hasPreview,
    onError: () => setFailed(true),
  };
}
